import type { AppScope } from '@/types'
import type { ModalFuncProps } from 'antd'
import type { TFunction } from 'i18next'
import { brandModalConfirmProps } from '@/components/BrandButton'

/** 唯一线上版本的权限快照（最多一个 released；取并集对数据异常也安全） */
export function getReleasedSnapshotKeys(
  versions: { status: string; scope_snapshot?: { approved: string[] } }[],
): Set<string> {
  return new Set(
    versions
      .filter((v) => v.status === 'released')
      .flatMap((v) => v.scope_snapshot?.approved ?? []),
  )
}

function ScopeLines({ scopes }: { scopes: AppScope[] }) {
  if (scopes.length === 0) return null
  return (
    <div className="scope-batch-confirm__list">
      {scopes.map((scope) => (
        <div key={scope.id} className="scope-batch-confirm__item">
          {`· ${scope.name}（${scope.scope_id}）`}
        </div>
      ))}
    </div>
  )
}

type ConfirmFn = (props: ModalFuncProps) => void

const CONFIRM_WIDTH = 580

const confirmLayout = {
  width: CONFIRM_WIDTH,
  styles: {
    container: { padding: '20px' },
  },
}

/** 单条关闭确认 */
export function confirmCloseScope(
  modal: { confirm: ConfirmFn },
  opts: {
    t: TFunction
    scope: AppScope
    onOk: () => void
  },
) {
  const { t, scope, onOk } = opts
  modal.confirm({
    title: t('scope.closeTitle'),
    content: t('scope.closeContent', { scopeId: scope.scope_id }),
    okText: t('scope.close'),
    cancelText: t('common.cancel'),
    ...confirmLayout,
    ...brandModalConfirmProps('danger'),
    onOk,
  })
}

/** 撤销待审核申请。确认后从列表移除，恢复为未申请。 */
export function confirmWithdrawScope(
  modal: { confirm: ConfirmFn },
  opts: {
    t: TFunction
    scope: AppScope
    onOk: () => void
  },
) {
  const { t, scope, onOk } = opts
  modal.confirm({
    title: t('scope.withdrawTitle'),
    content: t('scope.withdrawContent', { scopeId: scope.scope_id }),
    okText: t('scope.withdraw'),
    cancelText: t('common.cancel'),
    ...confirmLayout,
    ...brandModalConfirmProps('danger'),
    onOk,
  })
}

/** 删除已拒绝权限确认（Figma 10188:184115） */
export function confirmDeleteScope(
  modal: { confirm: ConfirmFn },
  opts: {
    t: TFunction
    scope: AppScope
    onOk: () => void
  },
) {
  const { t, scope, onOk } = opts
  modal.confirm({
    className: 'scope-delete-confirm',
    title: t('scope.deleteTitle', { name: scope.name }),
    content: (
      <div className="scope-delete-confirm__body">
        <div>{t('scope.deleteContent1')}</div>
        <div>{t('scope.deleteContent2')}</div>
      </div>
    ),
    okText: t('scope.deleteConfirmOk'),
    cancelText: t('common.cancel'),
    ...confirmLayout,
    ...brandModalConfirmProps('danger'),
    onOk,
  })
}

/** 批量关闭确认 */
export function confirmBatchCloseScopes(
  modal: { confirm: ConfirmFn },
  opts: {
    t: TFunction
    scopes: AppScope[]
    onOk: () => void
  },
) {
  const { t, scopes, onOk } = opts

  modal.confirm({
    className: 'scope-batch-confirm',
    title: t('scope.batchCloseTitle', { count: scopes.length }),
    content: (
      <div>
        <div className="scope-batch-confirm__desc">
          {t('scope.batchCloseContent')}
        </div>
        <ScopeLines scopes={scopes} />
      </div>
    ),
    okText: t('scope.batchClose'),
    cancelText: t('common.cancel'),
    ...confirmLayout,
    ...brandModalConfirmProps('danger'),
    onOk,
  })
}
